// components/sar/DetectionMarkers.jsx
import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { colorForSite } from '../../utils/SarSiteColors';
import { pass } from 'three/src/nodes/TSL.js';

// IMPORTANT: verify this against whatever lat/lon -> Vector3 convention
// SatelliteMarkers/Earth texture placement already uses. This is a
// standard equirectangular-mapping formula, but sign/axis conventions
// vary with texture.flipY and UV mapping choices - test against a
// known site (e.g. Rotterdam should appear over the actual North Sea
// coast, not offset) before trusting marker placement.

// const TEST_POINTS = [
//   { name: 'Equator / Prime Meridian', lat: 0, lon: 0 },
//   { name: 'North Pole', lat: 90, lon: 0 },
//   { name: 'South Pole', lat: -90, lon: 0 },
//   { name: 'Rotterdam', lat: 51.9244, lon: 4.4777 },
// ];

// TEST_POINTS.forEach(p => {
//   console.log(p.name, latLonToVec3(p.lat, p.lon));
// });


function latLonToVec3(lat, lon, radius = 1.01) {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = ((lon + 90) * Math.PI) / 180;
  const x = radius * Math.cos(latRad) * Math.sin(lonRad);
  const y = radius * Math.sin(latRad);
  const z = radius * Math.cos(latRad) * Math.cos(lonRad);
  return new THREE.Vector3(x, y, z);
}

const HALO_COLOR = new THREE.Color('#ffffff'); // neutral - color channel is already spoken for by site
const SELECTED_SCALE_MULT = 2.3;
const BASE_SCALE = 0.006;
const SELECTED_COLOR = new THREE.Color('#fbff00');
const UNCONFIRMED_COLOR = new THREE.Color('#f97316');

export function DetectionMarkers({ 
  detections,
  showUnconfirmed, 
  selectedSiteId, 
  onSelectSite 
}) {
  const meshRef = useRef();
  const haloRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const capacity = Math.max(detections.length, 1);

  useFrame(({ clock }) => {
    if (!meshRef.current || !haloRef.current) return;

    detections.forEach((d, i) => {
      const passesFilter = showUnconfirmed ? true : !d.unconfirmed;
      const isSelected = selectedSiteId && d.site_id === selectedSiteId;
      const scale = BASE_SCALE * (isSelected ? SELECTED_SCALE_MULT : 1);

      if (passesFilter) {
        dummy.position.copy(latLonToVec3(d.lat, d.lon));
        dummy.scale.setScalar(scale);
        dummy.lookAt(0, 0, 0);
      } else {
        dummy.scale.setScalar(0);
      }
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
      meshRef.current.setColorAt(
        i,
        isSelected ? SELECTED_COLOR : d.unconfirmed ? UNCONFIRMED_COLOR : colorForSite(d.site_id)
      );

      if (passesFilter && d.unconfirmed) {
        dummy.scale.setScalar(scale * 1.8);
      } else {
        dummy.scale.setScalar(0);
      }
      dummy.updateMatrix();
      haloRef.current.setMatrixAt(i, dummy.matrix);
      haloRef.current.setColorAt(i, HALO_COLOR);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
    haloRef.current.instanceMatrix.needsUpdate = true;
    if (haloRef.current.instanceColor) haloRef.current.instanceColor.needsUpdate = true;
  });

  const handleClick = (e) => {
    e.stopPropagation();
    const d = detections[e.instanceId];
    if (d) onSelectSite?.(d.site_id);
  };

  return (
    <group>
      <instancedMesh 
        key={`halo-${capacity}`} 
        ref={haloRef} 
        args={[null, null, capacity]}
      >
        <sphereGeometry args={[0.5, 8, 8]} />
        <meshBasicMaterial 
          toneMapped={false} transparent 
          opacity={0.35} 
          depthWrite={false} />
      </instancedMesh>

      <instancedMesh 
        key={`dots-${capacity}`} 
        ref={meshRef} 
        args={[null, null, capacity]} 
        onClick={handleClick}
        onPointerOver={() => (document.body.style.cursor = 'pointer')}
        onPointerOut={() => (document.body.style.cursor = 'default')}
      >
        {/* <sphereGeometry args={[1, 8, 8]} /> */}
        <sphereGeometry args={[0.5, 12, 12]} />
        <meshBasicMaterial toneMapped={false} />
      </instancedMesh>
    </group>
  );
}
